'use client';

import { KycVerificationSection } from '@verifime/components';
import { ReactElement } from 'react';
import { useCustomerConfidenceContext } from '../CustomerConfidence/CustomerConfidenceContext';
import { documentRows, TCustomerDocuments } from './documentRows';
import { documentsDecreasingConfidence } from './documentsDecreasingConfidence';

export type TKycVerificationSectionViewProps = {
  /**
   * The customer's identity documents, as the customer response carries them.
   *
   * A prop rather than a fetch, unlike the confidence level beside it, and the asymmetry is
   * deliberate. The page already holds the customer: it renders their name, their details card and
   * their risk from the same object, so asking for it again here would be a second request for
   * something already in memory. The confidence level is different because nothing else on the page
   * fetches it, and because two regions need it.
   */
  documents: TCustomerDocuments;
  /** When the customer record was last changed. Fills the table's `Last updated` column. */
  lastUpdatedOn?: string;
  'data-testid'?: string;
};

/**
 * The KYC/KYB section with its confidence outcome attached.
 *
 * **The detail half of a pair.** The same document level appears on the `Identity document(s)`
 * check card near the top of the page, which is the summary. Both read it from
 * `CustomerConfidenceProvider`, so one request serves both and they cannot light up at different
 * moments.
 *
 * **A view despite issuing no request itself.** It cannot be rendered by an application that knows
 * nothing about the person API, because it needs the provider above it, which is what separates a
 * view from a component here rather than the presence of a `fetch`. `DocumentTypeSelectionView`,
 * `FileUploaderView` and `PhotoGuideView` are views on the same basis, and ADR 74 is the decision
 * that puts view state in contexts.
 *
 * **Also where a customer's documents turn into table rows.** `documentRows` reads the API's own
 * document shapes, structured name fields and legacy whole-name fallback included, so it belongs in
 * this layer rather than in the presentational `KycVerificationSection` component it feeds: per
 * Charles's review of this component split, the components layer should stay free of any API-shaped
 * type, and mapping a response into rows is exactly that. `KycWithDocumentNames`'s row type is what
 * the component actually needs, so that is what crosses the boundary.
 *
 * **Loading and unavailable are both handled by passing nothing down.** The section still renders,
 * because the documents table has content regardless of whether an outcome could be determined, and
 * a client looking at a customer's documents should not lose the table because a level is missing.
 * `loading` draws the summary placeholder; `unavailable` and `error` draw the title alone.
 */
export default function KycVerificationSectionView({
  documents,
  lastUpdatedOn,
  'data-testid': dataTestId,
}: Readonly<TKycVerificationSectionViewProps>): ReactElement {
  const { document: documentAxis } = useCustomerConfidenceContext();

  return (
    <KycVerificationSection
      // The documents behind the level, from the same axis state and therefore the same response.
      // Read here rather than fetched separately, which is what makes the mark and the level arrive
      // together under the one `isLoadingOutcome`: a second request would let a row be accused
      // while the outcome accusing it was still a placeholder.
      //
      // **Translated, not handed over.** The response names its documents in the person API's
      // vocabulary and `documentRows` matches rows in the frontend's own, which is the whole reason
      // this seam exists: a component that knew the contract would stop marking anything, silently,
      // the day the contract moved. This layer is where the two are reconciled, so the day they
      // diverge is a compile error in `documentsDecreasingConfidence` rather than a lost mark.
      //
      // Absent unless the axis settled and the response actually named documents. Absence is not a
      // clean bill of health, it is the absence of an answer, and the table draws no mark for it,
      // which is the same thing it draws for a response that named none.
      rows={documentRows(
        documents,
        lastUpdatedOn,
        documentAxis.status === 'ready'
          ? documentsDecreasingConfidence(documentAxis.problemDocumentTypes)
          : undefined,
      )}
      // Only `ready` carries a level. `unavailable` and `error` deliberately collapse to no level
      // rather than to a distinct rendering: the section's job is the documents, and the difference
      // between "no outcome could be determined" and "the outcome could not be loaded" is already
      // stated on the check card above, where the client is looking for the outcome itself.
      level={documentAxis.status === 'ready' ? documentAxis.level : undefined}
      // Only the outcome. The documents arrived as a prop, so the table has its rows already and
      // skeletoning it here would hide content the page is holding.
      isLoadingOutcome={documentAxis.status === 'loading'}
      data-testid={dataTestId}
    />
  );
}
